import { CareEnvironment, CarePhase, FundingPolicyType } from '../../../../appModel';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH } from '../sharedConstants';
import { computeMonthlyCostMetrics, computeNon2AdlDaysThisRow } from '../sharedEligibility';
import {
  consumeWaitingPeriodDaysForEnvironment,
  getCalendarPolicyYear,
  resolveRowPolicyYear,
  getCareCostAnchorYear,
  getDailyBenefitAmountForEnvironment,
  getFirstContributingPhaseStartYear,
  getPhaseCareMonthsByPhase,
  growthFactor,
} from '../sharedPolicyConfig';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import {
  deriveIneligibilityReason,
  deriveIneligibleDayBreakdown,
} from '../deriveIneligibilityReason';
import { buildPrePolicyMonth } from '../buildPrePolicyMonth';
import { applyIndemnitySurplus, applyOpeningSurplusToCost } from '../sharedSurplus';
import {
  type Claimant,
  type JointWaitingPeriod,
  partnerWaitingPeriodSeed,
  seedJointWaitingPeriod,
} from '../jointClaimant';
import { CARE_MATTERS_ANNUITY_ACCELERATION_MONTHS } from '../../../../FundingPolicyTypeDef';

// ---------------------------------------------------------------------------
// Nationwide CareMatters Annuity (2943) / CareMatters Annuity Joint (2944).
//
// An ANNUITY chassis, despite sharing the CareMatters name with the life-funded
// CareMatters II. One single premium buys a contract value; the LTC benefit is a
// multiple of that value. This is why `isCareMattersFamily` deliberately does
// NOT cover these two types — cmTwoStrategy's flat base pool plus a separate
// inflation-rider stream is not how this product pays.
//
// Every rule below is cited to a page of a real carrier illustration. Page
// numbers are the PRINTED label ("p.6"), not the physical PDF page — the
// illustration staples on a generic life-insurance ledger with its own
// numbering. The full write-up, the ledger extracts each rule reproduces, and
// the 23 sampled illustrations live in:
//   calculations/docs/nationwide-carematters-annuity/README.md
//
// CONTRACT VALUE — the engine of the whole product (p.6-7 "Tabular Detail"):
//   CV(n) = CV(n-1) + interestCredit - riderCharges
// Guaranteed 3.00% interest, stated in the ledger header. The rider charge is a
// flat percentage of contract value set by risk class (0.425% Standard, 0.385%
// Preferred) and is "deducted monthly from the Contract Value" (rider
// disclosure ICC25-NWLA-686.1 p.4). Both are modelled monthly: interest lands
// slightly under a flat annual 3% precisely because the charge comes off first.
//
// TWO POOLS (p.5 "How CareMatters Annuity works"):
//   LTC Acceleration     always 1x CV, over 24 months single / 36 joint
//   Extension of Benefits  1x CV (Standard) or 2x CV (Preferred), over the rest
// so the total is 2x CV (Standard) or 3x CV (Preferred), across 48/72 months
// single and 72/108 joint. The acceleration WINDOW is constant across risk
// classes; only the extension lengthens.
//
// MONTHLY CAP — the part that makes this its own strategy:
//   monthlyCap = contractValue / accelerationMonths
// It is NOT fixed at issue. The pool is a multiple of a compounding contract
// value, so the benefit grows before claim even with no inflation rider elected
// (p.5: "prior to the first benefit payment, the maximum monthly benefit may
// increase if the annuity Contract Value increases"). Day-1 monthly is
// identical in both risk classes — $4,165 single, $2,777 joint on a $100,000
// premium — because the multiple and the period scale together.
//
// INFLATION RIDER — a FLOOR, not a growth rate (p.6 footnote):
//   "This maximum monthly benefit value reflects the greater of the benefit
//    determined by contract value and the inflation protection benefit for
//    that year."
// so
//   monthlyCap(n) = max( CV(n) / accelerationMonths,
//                        dayOneMonthly * 1.05^(n-1) )
// The contract-value leg wins early and the inflation leg takes over at a
// crossover year that moves with issue age (year 3 for Female/60). Modelling
// the rider as pool growth instead would be wrong in both directions: too high
// before the crossover, too low after.
//
// The rider is also not a premium add-on. It is a one-off year-1 charge against
// the contract value — 48,000 of a 100,000 premium — which is why electing it
// LOWERS the day-1 benefit (p.6: year-1 rider charges 48,221, contract value
// 53,335). Nonforfeiture works the same way at 1,500. Both are already baked
// into the contract value this strategy is handed, so neither is re-applied
// here.
//
// PAYOUT: always CASH INDEMNITY (p.4). "Nationwide places no restrictions on
// how benefits are used"; 100% may be used for informal care. A fixed product
// property, gated on policyType rather than on the persisted
// `isIndemnityPolicyPayment` column, the same treatment SecureCare IV uses — it
// is not a user election and gating on the column would leave hand-entered
// policies silently on the reimbursement path.
//
// ELIMINATION PERIOD: 90 calendar days, RETROACTIVE (p.5). "benefits for the
// first 90 days will be paid retroactively along with benefits for month 4",
// making the first payment 4x monthly. Also a fixed product property.
//
// RIDER CHARGE WAIVER: stated in the rider disclosure (form ICC25-NWLA-686.1,
// p.2 of 4) — "The monthly Rider charge is waived when a Rider benefit has been
// paid during the prior Contract Month." Note PRIOR: the waiver lags the benefit
// by one month, so the first month of a claim is still charged and the month
// after a claim ends is not.
//
// DEATH BENEFIT: the remaining contract value. The ledger column is a single
// "Contract Value / Death Benefit" and p.4 guarantees a "Death Benefit of any
// remaining contract value not used for LTC benefits". There is no guaranteed
// MINIMUM — leave `policyMinimumGuaranteedBenefitAmount` null; that slot is
// ForeCare's floor and this product has none. Acceleration draws deplete the
// contract value dollar for dollar, so the death benefit erodes to zero across
// the first 24/36 claim months and a claim reaching the extension layer leaves
// beneficiaries nothing (p.9).
// ---------------------------------------------------------------------------

export interface CareMattersAnnuityState {
  // Indemnity dollars paid above eligible cost, carried forward and drained on a
  // later shortfall month. Always a live reserve here — the product is always
  // indemnity, so unlike a reimbursement policy this is never a constant 0.
  indemnitySurplus: number;
  // True once the retroactive EP lump has fired. The EP is reimbursed once per
  // claim; a second WP bucket satisfying later must not fire it again.
  // On a joint contract this is the ACTIVE claimant's latch — see
  // `jointEpReimbursed`.
  epReimbursed: boolean;
  // 2-ADL-qualified days WITHHELD from payment so far. This — not
  // `initialWp - closeWp` — is what the retroactive lump reimburses, because a
  // paid zero-WP home-care day still decrements the facility bucket and must
  // not be paid twice. On a joint contract this is the ACTIVE claimant's tally.
  unpaidWpDaysServed: number;
  // JOINT ONLY. Each insured serves their OWN 90-day elimination period and
  // earns their OWN retroactive reimbursement — `686.1 p.3`: "The Elimination
  // Period must be satisfied by each Insured." A single shared set of these
  // would let the second spouse start drawing the moment the first satisfied,
  // and would deny them their lump entirely. Undefined on single life, which
  // uses the universal slots above.
  jointWaitingPeriod?: JointWaitingPeriod;
  jointEpReimbursed?: Record<Claimant, boolean>;
  jointUnpaidWpDaysServed?: Record<Claimant, number>;
  // Whether a benefit was paid in the PRIOR month, which is what the rider
  // disclosure gates the charge waiver on — not the current month. See the
  // waiver note in computeMonth.
  benefitPaidLastMonth: boolean;
  // The monthly cap fixed at claim start: contract value on the date of the
  // first benefit payment, divided by the acceleration factor. Held in state
  // because the contract sets it ONCE — it must not follow the contract value
  // down as benefits deplete it. See the cap note in computeMonth.
  claimStartMonthlyCap: number;
}

export interface CareMattersAnnuityDeltas {
  contractValueInterestCredit: number;
  contractValueRiderCharge: number;
  acceleratedBenefitDraw: number;
  extendedBenefitDraw: number;
  indemnitySurplusAccrued: number;
  indemnitySurplusApplied: number;
  epLumpSumPaid: number;
  // The portion of the lump that covered THIS row's cost. Without it the shared
  // coverage rollup falls back to counting the entire lump against cost, which
  // double-counts the excess already banked as surplus.
  epLumpSumAppliedToCost: number;
  // Which layer each half of the lump came out of. Only the acceleration half
  // reduces the contract value / death benefit; the extension half does not.
  // The projection table needs both to make its balance arithmetic reconcile on
  // the reimbursement row.
  epLumpSumBasePaid: number;
  epLumpSumEobPaid: number;
  // Surfaced so the projection table can explain the lump rather than just show
  // a jump in the paid column.
  epLumpSumDays: number;
  epLumpSumDailyRate: number;
}

// p.5 — shared with the quote mappers so both agree on the window.
const { single: ACCELERATION_MONTHS_SINGLE, joint: ACCELERATION_MONTHS_JOINT } =
  CARE_MATTERS_ANNUITY_ACCELERATION_MONTHS;

// Ledger header, every sampled illustration.
const GUARANTEED_INTEREST_RATE = 0.03;

// Annual rider charge as a percentage of contract value, from the ledger's Rider
// Charges column (p.6-7 of the four no-rider illustrations). Constant across
// policy years — it is a flat rate on a moving balance, not a schedule.
//
//   single Standard   425 / 100,000
//   single Preferred  385 / 100,000
//   joint  Standard   349 / 100,000
//   joint  Preferred  314 / 100,000
//
// Keyed on (joint, multiple) rather than on risk class directly, because RISK
// CLASS IS NOT STORED ANYWHERE ON A FUNDING SOURCE. The quote carries it in
// `primaryTobaccoStatus` — Standard maps to Non-Tobacco, Preferred to Tobacco,
// the inverse of the usual convention — and that column does not survive onto
// the policy.
//
// It does not need to. The class is fully recoverable from the benefit period,
// which every entry path already carries:
//
//   benefitPeriodMonths / accelerationMonths = the LTC multiple = the class
//   48/24 = 2 Standard   72/24 = 3 Preferred   (single)
//   72/36 = 2 Standard  108/36 = 3 Preferred   (joint)
//
// so this works for shop-added, hand-entered and PDF-uploaded policies alike,
// with no extra field and without the calc layer reaching into `sourceQuote`.
// An explicit `policyMonthlyContinuationBenefitChargeRate` still wins when set.
const ANNUAL_RIDER_CHARGE_RATE: Record<'single' | 'joint', Record<2 | 3, number>> = {
  single: { 2: 0.00425, 3: 0.00385 },
  joint: { 2: 0.00349, 3: 0.00314 },
};

// Anything other than a clean 2x or 3x means the benefit period and the
// acceleration window disagree — a part-drawn or hand-mistyped policy. Charge
// nothing rather than guess: a wrong rate compounds over 30+ years, while a
// missing one leaves the contract value on its stated 3% and errs by well under
// half a percent a year.
function resolveAnnualRiderChargeRate(
  isJoint: boolean,
  benefitPeriodMonths: number,
  accelerationMonths: number,
): number {
  if (accelerationMonths <= 0) return 0;
  const ratio = benefitPeriodMonths / accelerationMonths;
  // Must land ON 2x or 3x, not merely round to one. A 60-month single-life
  // policy gives 2.5, which rounds to 3 and would silently take the Preferred
  // rate for a policy that is neither class.
  for (const multiple of [2, 3] as const) {
    if (Math.abs(ratio - multiple) < 1e-6) {
      return ANNUAL_RIDER_CHARGE_RATE[isJoint ? 'joint' : 'single'][multiple];
    }
  }
  return 0;
}

function isJointCareMattersAnnuity(ltcPolicy: PolicyFundingSource): boolean {
  return ltcPolicy.policyType === FundingPolicyType.jointCareMattersAnnuity;
}

function isFiniteNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function readPositive(value: number | null | undefined, fallback: number): number {
  return isFiniteNumber(value) && value > 0 ? value : fallback;
}

export interface CareMattersAnnuityConfig {
  contractValueAtIssue: number;
  accelerationMonths: number;
  extensionMonths: number;
  benefitPeriodMonths: number;
  guaranteedInterestRate: number;
  monthlyRiderChargeRate: number;
  dayOneMonthlyCap: number;
  inflationRate: number;
  inflationCapYears: number;
  inflationIsSimple: boolean;
}

// Resolves the product's shape from a funding source.
//
// Prefers the rate-based slots a shop-added quote populates, and falls back to
// the plain pool fields so a HAND-ENTERED policy also projects correctly — the
// acceleration pool is recoverable as `total - continuation`, which is the same
// identity the quote mappers use in reverse.
export function readCareMattersAnnuityConfig(
  ltcPolicy: PolicyFundingSource,
): CareMattersAnnuityConfig {
  const isJoint = isJointCareMattersAnnuity(ltcPolicy);
  const accelerationMonths = readPositive(
    ltcPolicy.policyAccelerationBenefitDurationMonths,
    isJoint ? ACCELERATION_MONTHS_JOINT : ACCELERATION_MONTHS_SINGLE,
  );

  const totalPool = ltcPolicy.policyMaximumBenefitAmount ?? 0;
  const continuationPool = ltcPolicy.policyContinuationBenefitAmount ?? 0;
  const contractValueAtIssue = Math.max(
    ltcPolicy.policyAccumulatedValueAtIssue ??
      ltcPolicy.policyBaseBenefitAmountAtIssue ??
      totalPool - continuationPool,
    0,
  );

  const extensionAtIssue = Math.max(
    ltcPolicy.policyContinuationBenefitAmountAtIssue ?? continuationPool,
    0,
  );

  // Extension months, in order of trust:
  //
  //   1. the stored extension duration, when the policy carries one;
  //   2. otherwise `benefitPeriodMonths - accelerationMonths`, because the
  //      contractual total period is a DURATION and cannot be distorted by
  //      inflation;
  //   3. only as a last resort, the ratio of the dollar pools.
  //
  // (3) must not be preferred over (2). On an inflation-elected quote row the
  // continuation pool is built as `ltcBenefitTotal - contractValue`, and
  // `ltcBenefitTotal` already embeds inflation accrued DURING the claim (see
  // resolveContractValue in transformCareMattersAnnuityCSV.ts), so the ratio is
  // inflated too. The documented 51,984 / 112,024 example yields 28 extension
  // months instead of 24 — and 24 + 28 = 52 against a contractual 48, so the
  // pools no longer reconcile with the benefit period.
  const storedBenefitPeriodMonths = readPositive(ltcPolicy.policyBenefitPeriodMonths, 0);
  const extensionFromBenefitPeriod =
    storedBenefitPeriodMonths > accelerationMonths
      ? storedBenefitPeriodMonths - accelerationMonths
      : 0;
  const extensionFromPools =
    contractValueAtIssue > 0
      ? Math.max(Math.round((extensionAtIssue / contractValueAtIssue) * accelerationMonths), 0)
      : 0;
  const extensionMonths = readPositive(
    ltcPolicy.policyEobBenefitDurationMonths,
    extensionFromBenefitPeriod > 0 ? extensionFromBenefitPeriod : extensionFromPools,
  );

  const benefitPeriodMonths = readPositive(
    ltcPolicy.policyBenefitPeriodMonths,
    accelerationMonths + extensionMonths,
  );

  const inflationCapYearsRaw = ltcPolicy.policyInflationProtectionYears;

  return {
    contractValueAtIssue,
    accelerationMonths,
    extensionMonths,
    benefitPeriodMonths,
    guaranteedInterestRate: readPositive(
      ltcPolicy.policyAccumulatedValueGuaranteedInitialRate,
      GUARANTEED_INTEREST_RATE,
    ),
    monthlyRiderChargeRate: isFiniteNumber(ltcPolicy.policyMonthlyContinuationBenefitChargeRate)
      ? ltcPolicy.policyMonthlyContinuationBenefitChargeRate
      : resolveAnnualRiderChargeRate(isJoint, benefitPeriodMonths, accelerationMonths) / 12,
    dayOneMonthlyCap: accelerationMonths > 0 ? contractValueAtIssue / accelerationMonths : 0,
    inflationRate: ltcPolicy.policyInflationProtection ?? 0,
    inflationCapYears: inflationCapYearsRaw && inflationCapYearsRaw > 0 ? inflationCapYearsRaw : 0,
    inflationIsSimple: ltcPolicy.isSimpleInflationProtection === true,
  };
}

function monthlyEffectiveRate(annualRate: number): number {
  return Math.pow(1 + annualRate, 1 / 12) - 1;
}

function normalizedPolicyYear(policyYear: number): number {
  return Math.max(Math.floor(policyYear), 1);
}

function inflationFloorFactor(config: CareMattersAnnuityConfig, policyYear: number): number {
  return growthFactor(
    config.inflationRate,
    config.inflationCapYears,
    config.inflationIsSimple,
    policyYear,
  );
}

// The monthly cap in a given policy year: the greater of what the contract value
// supports and the inflation floor (p.6 footnote).
function monthlyCapFor(
  config: CareMattersAnnuityConfig,
  contractValue: number,
  policyYear: number,
): number {
  const fromContractValue =
    config.accelerationMonths > 0 ? contractValue / config.accelerationMonths : 0;
  const fromInflation = config.dayOneMonthlyCap * inflationFloorFactor(config, policyYear);
  return Math.max(fromContractValue, fromInflation);
}

// Which instant within a policy year to sample. This matters more than it
// sounds, because the two legs of the cap move differently: the contract value
// grows continuously while the inflation benefit steps once, on the anniversary.
// So the contract-value leg leads WITHIN a year and can be overtaken by the
// following year's inflation step.
//
//   'end'   the carrier's own convention. The illustration's Tabular Detail
//           footnote states its figures are for "a claim that starts payments in
//           the final month of the projection year", and sampling here
//           reproduces the printed Max Monthly column to the dollar across every
//           row: 2,222 / 2,279 / 2,388 / 2,507 / 2,633 for Female/60/Standard
//           with the rider. Use for anything shown to an advisor as "what this
//           policy is worth in year N".
//   'start' the conservative instant, immediately after the inflation step and
//           before any of that year's contract-value growth. Use when seeding a
//           claim that begins in this year — the claimant has not yet earned the
//           year's interest.
//
// Sampling at 'start' makes the inflation leg win in every year (both legs carry
// the same number of steps), which is why the crossover is only visible at 'end'.
export type CareMattersAnnuitySample = 'start' | 'end';

// Rolls the contract value forward to the requested instant in `policyYear`,
// then reports the pools that value implies.
//
// The projection only spans claim months, so pre-claim anniversaries are never
// iterated by the engine — this replays them. Charge first, then interest, both
// monthly, matching the ledger (p.6): the value is floored at its opening
// balance so a charge-heavy month can never run it backwards.
export function scheduledCareMattersAnnuityValues(
  config: CareMattersAnnuityConfig,
  policyYear: number,
  sample: CareMattersAnnuitySample = 'end',
): {
  contractValue: number;
  acceleratedBenefit: number;
  maximumExtendedBenefit: number;
  totalLtcBenefit: number;
  monthlyBenefitCap: number;
} {
  const targetYear = normalizedPolicyYear(policyYear);
  const monthlyRate = monthlyEffectiveRate(config.guaranteedInterestRate);
  // Full years of growth: a year-end sample has earned this year's interest, a
  // year-start sample has not.
  const growthYears = sample === 'end' ? targetYear : targetYear - 1;
  let contractValue = config.contractValueAtIssue;

  for (let year = 0; year < growthYears; year += 1) {
    for (let month = 0; month < 12; month += 1) {
      const openValue = contractValue;
      const riderCharge = contractValue * config.monthlyRiderChargeRate;
      const afterCharge = Math.max(contractValue - riderCharge, 0);
      contractValue = Math.max(afterCharge * (1 + monthlyRate), openValue);
    }
  }

  // The inflation leg carries `targetYear - 1` steps either way — it stepped on
  // this year's anniversary and does not move again until the next one.
  const monthlyBenefitCap = monthlyCapFor(config, contractValue, targetYear);

  // Both layers pay at the same cap, so each pool is that cap across its own
  // months. Sizing the extension this way — rather than holding it at a fixed
  // dollar amount — is what carries the inflation floor into the extension
  // layer, which the ledger's Total LTC Benefit column requires.
  return {
    contractValue,
    acceleratedBenefit: monthlyBenefitCap * config.accelerationMonths,
    maximumExtendedBenefit: monthlyBenefitCap * config.extensionMonths,
    totalLtcBenefit: monthlyBenefitCap * config.benefitPeriodMonths,
    monthlyBenefitCap,
  };
}

function emptyDeltas(): CareMattersAnnuityDeltas {
  return {
    contractValueInterestCredit: 0,
    contractValueRiderCharge: 0,
    acceleratedBenefitDraw: 0,
    extendedBenefitDraw: 0,
    indemnitySurplusAccrued: 0,
    indemnitySurplusApplied: 0,
    epLumpSumPaid: 0,
    epLumpSumAppliedToCost: 0,
    epLumpSumBasePaid: 0,
    epLumpSumEobPaid: 0,
    epLumpSumDays: 0,
    epLumpSumDailyRate: 0,
  };
}

interface EpLumpResult {
  epLumpSumPaid: number;
  epLumpSumBasePaid: number;
  epLumpSumEobPaid: number;
  epLumpSumDays: number;
  fired: boolean;
}

// Retroactive elimination period (p.5). Follows SecureCare IV's lump, which in
// turn follows CM II's: fire once on the satisfaction edge, fund the days
// actually WITHHELD from payment, and spill Acceleration -> Extension exactly as
// a regular row does.
//
// The days funded are `unpaidWpDaysServed`, not `initialWp - closeWp`. The two
// coincide for the ordinary case — 90 withheld days at monthly/30 gives three
// monthly payments, which with the current month's payment is the illustration's
// "4 x $8,056" first payment — but diverge on an asymmetric claim, where paid
// home-care days burn the facility bucket and `initial - close` would pay them
// twice.
function computeEpRetroactiveLump(input: {
  openRelevantWp: number;
  closeRelevantWp: number;
  unpaidWpDaysServed: number;
  baseAfterDraw: number;
  eobAfterDraw: number;
  monthlyCap: number;
  alreadyReimbursed: boolean;
}): EpLumpResult {
  const empty: EpLumpResult = {
    epLumpSumPaid: 0,
    epLumpSumBasePaid: 0,
    epLumpSumEobPaid: 0,
    epLumpSumDays: 0,
    fired: false,
  };

  const epJustSatisfied = input.openRelevantWp > 0 && input.closeRelevantWp === 0;
  if (!epJustSatisfied || input.alreadyReimbursed) return empty;

  // Past this point the EP is satisfied on this row, so the latch MUST set even
  // when nothing can be funded (exhausted pools, zero withheld days) — otherwise
  // a later bucket satisfying would fire a second lump.
  const satisfiedButUnfunded: EpLumpResult = { ...empty, fired: true };

  const daysServed = Math.max(input.unpaidWpDaysServed, 0);
  const daily = input.monthlyCap / DAYS_IN_MONTH;
  if (daysServed <= 0 || daily <= 0) return satisfiedButUnfunded;

  const baseDaysFunded = Math.min(daysServed, input.baseAfterDraw / daily);
  const epLumpSumBasePaid = Math.min(daily * baseDaysFunded, input.baseAfterDraw);

  const eobDaysFunded = Math.min(daysServed - baseDaysFunded, input.eobAfterDraw / daily);
  const epLumpSumEobPaid = Math.min(daily * eobDaysFunded, input.eobAfterDraw);

  const daysFunded = baseDaysFunded + eobDaysFunded;
  if (daysFunded <= 0) return satisfiedButUnfunded;

  return {
    epLumpSumPaid: epLumpSumBasePaid + epLumpSumEobPaid,
    epLumpSumBasePaid,
    epLumpSumEobPaid,
    epLumpSumDays: daysFunded,
    fired: true,
  };
}

export const careMattersAnnuityStrategy: PolicyProjectionStrategy<
  CareMattersAnnuityState,
  CareMattersAnnuityDeltas
> = {
  initClaimState(client, ltcPolicy, jointAnchorPolicyYear): MonthlyState<CareMattersAnnuityState> {
    const isJoint = ltcPolicy.policyType === FundingPolicyType.jointCareMattersAnnuity;
    const config = readCareMattersAnnuityConfig(ltcPolicy);
    // Seed at the EARLIEST claimant's year on a joint contract. `computeMonth`
    // resolves every row against `jointAnchorPolicyYear`, so deriving the seed
    // from the session client instead opens the pools at a later year than the
    // ledger starts — the contract value has been compounding 3% a year, so the
    // shared pools, claim-start cap, death benefit and prior-utilization terms
    // all come out high when the PARTNER claims first.
    //
    // Same `jointAnchorPolicyYear ?? getCalendarPolicyYear(...)` shape the other
    // joint-aware strategies use (generic hybrid, hybrid life IRA, OA Asset
    // Care, traditional LTC).
    const claimStartPolicyYear =
      jointAnchorPolicyYear ?? getCalendarPolicyYear(client as Client, 0);
    // 'start': the claim opens now, before this year's interest is credited.
    const scheduled = scheduledCareMattersAnnuityValues(config, claimStartPolicyYear, 'start');

    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const acceleratedUtilized = Math.min(utilizedToDate, scheduled.acceleratedBenefit);
    const extendedUtilized = Math.max(utilizedToDate - scheduled.acceleratedBenefit, 0);

    // The `...DaysRemaining` fields describe the PRIMARY client's in-flight
    // claim, so only they may open mid-EP; the partner always serves the full
    // period (see partnerWaitingPeriodSeed).
    const clientWaitingPeriod = {
      homeDays:
        ltcPolicy.homeCareWaitingPeriodDaysRemaining ?? ltcPolicy.homeCareWaitingPeriodDays ?? 0,
      facilityDays:
        ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
        ltcPolicy.facilityCareWaitingPeriodDays ??
        0,
    };

    return {
      pools: {
        baseBenefitPool: Math.max(scheduled.acceleratedBenefit - acceleratedUtilized, 0),
        extensionOfBenefitsPool: Math.max(
          scheduled.maximumExtendedBenefit - extendedUtilized,
          0,
        ),
        // The death benefit IS the contract value, depleted by acceleration
        // draws. Prior utilization has already consumed that much of it.
        deathBenefit: Math.max(scheduled.contractValue - acceleratedUtilized, 0),
      },
      waitingPeriod: clientWaitingPeriod,
      strategyState: {
        indemnitySurplus: 0,
        epReimbursed: ltcPolicy.epReimbursed === true,
        unpaidWpDaysServed: 0,
        // A policy entering the projection with benefits already drawn was on
        // claim last month, so its first modelled month inherits the waiver.
        benefitPaidLastMonth: utilizedToDate > 0,
        claimStartMonthlyCap: scheduled.monthlyBenefitCap,
        // Each insured serves their own EP and earns their own lump.
        ...(isJoint
          ? {
              jointWaitingPeriod: seedJointWaitingPeriod(
                clientWaitingPeriod,
                partnerWaitingPeriodSeed(ltcPolicy),
              ),
              jointEpReimbursed: {
                client: ltcPolicy.epReimbursed === true,
                partner: false,
              },
              jointUnpaidWpDaysServed: { client: 0, partner: 0 },
            }
          : {}),
      },
    };
  },

  buildClaimTerms(client, ltcPolicy, jointAnchorPolicyYear): ClaimTerms {
    const config = readCareMattersAnnuityConfig(ltcPolicy);
    const clientAsClient = client as Client;
    // Must use the SAME anchor as initClaimState — see the note there.
    const claimStartPolicyYear =
      jointAnchorPolicyYear ?? getCalendarPolicyYear(clientAsClient, 0);
    // Must match initClaimState's instant, or "drawn to date" diverges from what
    // was actually deducted from the pools.
    const scheduled = scheduledCareMattersAnnuityValues(config, claimStartPolicyYear, 'start');
    const projectionAnchorYear = getCareCostAnchorYear(clientAsClient);

    // Prior utilization across Acceleration then Extension at claim start,
    // mirroring initClaimState. The contract value is a separate concern and
    // does not feed "drawn to date".
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const acceleratedUtilized = Math.min(utilizedToDate, scheduled.acceleratedBenefit);
    const extendedUtilized = Math.min(
      Math.max(utilizedToDate - scheduled.acceleratedBenefit, 0),
      scheduled.maximumExtendedBenefit,
    );

    const dailyBenefit = scheduled.monthlyBenefitCap / DAYS_IN_MONTH;

    return {
      strategyId: 'carematters-annuity',
      priorUtilization: acceleratedUtilized + extendedUtilized,
      nominalDailyBenefit: dailyBenefit,
      nominalDailyBenefitBase: dailyBenefit,
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: ltcPolicy.dailyHomeBenefitAmount ?? 0,
        [CareEnvironment.assistedLiving]: ltcPolicy.dailyAssistedLivingBenefitAmount ?? 0,
        [CareEnvironment.fullCareFacility]: ltcPolicy.dailyNursingHomeBenefitAmount ?? 0,
        [CareEnvironment.independentLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.independentLiving,
        ),
      },
      inflationRate: config.inflationRate,
      inflationCapYears: config.inflationCapYears,
      inflationIsSimple: config.inflationIsSimple,
      // Neither pool grows by a rider rate of its own — both track the monthly
      // cap, which is driven by the contract value and floored by inflation.
      baseBenefitPoolInflationRate: 0,
      baseBenefitPoolInflationCapYears: 0,
      extensionOfBenefitsPoolInflationRate: config.inflationRate,
      extensionOfBenefitsPoolInflationCapYears: config.inflationCapYears,
      baseBenefitPoolAtIssue: config.contractValueAtIssue,
      extensionOfBenefitsPoolAtIssue:
        config.dayOneMonthlyCap * config.extensionMonths,
      baseBenefitPoolDurationMonths: config.accelerationMonths,
      extensionOfBenefitsPoolDurationMonths: config.extensionMonths,
      accumulatedValueAtIssue: config.contractValueAtIssue,
      accumulatedValueGuaranteedInitialRate: config.guaranteedInterestRate,
      monthlyContinuationBenefitChargeRate: config.monthlyRiderChargeRate,
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      phaseCareMonthsByPhase: getPhaseCareMonthsByPhase(clientAsClient),
      firstPhaseStartYear: getFirstContributingPhaseStartYear(clientAsClient),
      policyPremiumStartYear: ltcPolicy.policyPremiumStartYear ?? projectionAnchorYear,
      // No guaranteed minimum — the residual is whatever contract value is left.
      minimumGuaranteedDeathBenefit: 0,
      isReimbursement: false,
      isIndemnity: true,
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    const { ltcPolicy, policyYear } = input;
    const config = readCareMattersAnnuityConfig(ltcPolicy);
    // 'end' — the carrier's own framing for a year's printed values.
    const scheduled = scheduledCareMattersAnnuityValues(config, policyYear, 'end');
    const daily = scheduled.monthlyBenefitCap / DAYS_IN_MONTH;

    return {
      policyYear,
      effectiveDailyBenefit: daily,
      effectiveDailyBenefitBase: daily,
      effectiveDailyBenefitRider: daily,
      // The "face" here is the acceleration layer, which is also the death
      // benefit's backing.
      effectiveFaceAmount: scheduled.acceleratedBenefit,
      // The real account value, which the acceleration pool is only equal to
      // while the inflation floor is not binding. The growth table shows this
      // as "Contract value"; the pool figure above is what the cap multiplies out to.
      contractValue: scheduled.contractValue,
      effectiveContinuationBenefit: scheduled.maximumExtendedBenefit,
      effectiveTotalLtcBenefit: scheduled.totalLtcBenefit,
      monthlyBenefitCap: scheduled.monthlyBenefitCap,
      continuationMonthlyBenefitCap: scheduled.monthlyBenefitCap,
      theoreticalLtcPoolCeiling: scheduled.totalLtcBenefit,
    };
  },

  computeMonth(
    input: MonthComputeInput<CareMattersAnnuityState, CareMattersAnnuityDeltas>,
  ): MonthlyEntry<CareMattersAnnuityState, CareMattersAnnuityDeltas> {
    const {
      client,
      ltcPolicy,
      phaseCarePlan,
      monthlyCareInput,
      openState,
      priorMonth,
      priorClaimantMonth,
    } = input;
    const { monthIndex, carePhase, monthsIntoPhase, claimant } = monthlyCareInput;
    const clientAsClient = client as Client;

    // Joint-ness is read from the CARRIED STATE, not the row tag, so both
    // spouses' balances survive every row. Each insured serves their own 90-day
    // elimination period and earns their own retroactive lump — `686.1 p.3`.
    const jointWaitingPeriodBefore = openState.strategyState.jointWaitingPeriod;
    const isJointRun = jointWaitingPeriodBefore != null;
    const activeClaimant: Claimant = claimant ?? 'client';
    const activeWaitingPeriodBefore = jointWaitingPeriodBefore
      ? jointWaitingPeriodBefore[activeClaimant]
      : openState.waitingPeriod;
    const activeEpReimbursed = isJointRun
      ? (openState.strategyState.jointEpReimbursed?.[activeClaimant] ?? false)
      : openState.strategyState.epReimbursed;
    const activeUnpaidWpDaysServed = isJointRun
      ? (openState.strategyState.jointUnpaidWpDaysServed?.[activeClaimant] ?? 0)
      : openState.strategyState.unpaidWpDaysServed;

    const costMetrics = computeMonthlyCostMetrics(clientAsClient, phaseCarePlan, monthsIntoPhase);
    const claimYearOffset = Math.floor(monthIndex / 12);
    // Joint runs share ONE policy-year clock anchored at the earliest spouse's
    // claim start. Deriving it from the row's own client instead puts the two
    // spouses on different clocks, so anniversary inflation and the pre-policy
    // gate fire at different times for each. Falls back to the calendar year on
    // single-life, where `jointAnchorPolicyYear` is undefined.
    const policyYear = resolveRowPolicyYear(
      clientAsClient,
      monthIndex,
      input.jointAnchorPolicyYear,
    );

    const context: MonthContext = {
      monthIndex,
      policyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, policyYear),
      claimYearOffset,
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      claimant: monthlyCareInput.claimant,
    };

    if (policyYear < 1) {
      const prePolicyFacts: MonthlyCareFacts = {
        nominalCareCost: costMetrics.nominalCareCost,
        effectiveCareCost: costMetrics.effectiveCareCost,
        dailyCareCost: costMetrics.dailyCareCost,
        careCostInflationYears: costMetrics.careCostInflationYears,
        projectedCareYear: costMetrics.projectedCareYear,
        non2AdlDays: 0,
        coverageEligibleDays: 0,
        waitingPeriodDaysConsumedHome: 0,
        waitingPeriodDaysConsumedFacility: 0,
        appliedMonthlyBenefitCap: 0,
        appliedCapPoolSource: 'base',
      };
      return buildPrePolicyMonth<CareMattersAnnuityState, CareMattersAnnuityDeltas>({
        context,
        facts: prePolicyFacts,
        openState,
        strategyDeltas: emptyDeltas(),
      });
    }

    const config = readCareMattersAnnuityConfig(ltcPolicy);

    // 1. Day classification (2-ADL bucket -> waiting period -> eligible).
    const non2AdlDays = computeNon2AdlDaysThisRow(clientAsClient, phaseCarePlan, monthsIntoPhase);
    const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDays;

    const {
      wpDaysThisRow,
      closeHomeWp,
      closeFacilityWp,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      closeRelevantWp,
    } = consumeWaitingPeriodDaysForEnvironment(
      activeWaitingPeriodBefore,
      phaseCarePlan.appliedCareEnvironment,
      daysAfter2Adl,
    );
    const eligibleDays = daysAfter2Adl - wpDaysThisRow;

    // 2. This month's cap. FIXED AT CLAIM START, not tracked to the live
    // contract value. Rider disclosure ICC25-NWLA-686.1 p.2 of 4:
    //
    //   LTC Rider: "the maximum monthly benefit ... will equal the Contract
    //   Value ON THE DATE OF THE FIRST BENEFIT PAYMENT divided by the LTC
    //   Acceleration Factor."
    //
    //   LTC With Inflation Rider: "...On each Contract Anniversary afterwards,
    //   the maximum monthly benefit will at least be equal to the maximum
    //   monthly benefit on the Date of Issue increased at 5% annual compound
    //   interest."
    //
    // Reading the live contract value here instead would let the cap DECAY as
    // benefits deplete that value — on a claim opening in policy year 27 it
    // fell from 8,046 to 6,034 over six months, understating the benefit by a
    // quarter and worsening every month. The day-one floor masked it entirely
    // for claims starting in policy year 1, which is what the tests used.
    //
    // The inflation leg is a floor measured from the DATE OF ISSUE cap, exactly
    // as the clause words it, so it is still evaluated per policy year.
    const capForPolicyYear = (year: number) =>
      Math.max(
        openState.strategyState.claimStartMonthlyCap,
        config.dayOneMonthlyCap * inflationFloorFactor(config, year),
      );
    const monthlyCap = capForPolicyYear(policyYear);
    const proRatedCap = monthlyCap * (eligibleDays / DAYS_IN_MONTH);
    const eligibleCost = costMetrics.dailyCareCost * eligibleDays;

    // 2b. The Extension balance rises with the cap on each anniversary. Same
    // clause, ICC25-NWLA-686.1 p.2 of 4:
    //
    //   "The Remaining Maximum Amount of LTC Extension Benefits Payable will
    //    increase by the same proportion as the maximum monthly benefit on each
    //    Contract Anniversary."
    //
    // Only the EXTENSION balance — the acceleration layer IS the contract value
    // and is being drawn down, not inflated.
    //
    // Without this the pool is frozen at its claim-start size while the cap
    // keeps stepping 5%, so it buys fewer and fewer months: measured on a
    // year-27 claim with the rider, 22 extension months instead of 24 and 45
    // paying months instead of 48 — roughly three months of benefit lost.
    let eobInflationAccrual = 0;
    if (
      priorMonth &&
      policyYear !== priorMonth.context.policyYear &&
      openState.pools.extensionOfBenefitsPool > 0
    ) {
      const capPrior = capForPolicyYear(priorMonth.context.policyYear);
      if (monthlyCap > capPrior && capPrior > 0) {
        eobInflationAccrual =
          openState.pools.extensionOfBenefitsPool * (monthlyCap / capPrior - 1);
      }
    }
    const eobAfterAccrual = openState.pools.extensionOfBenefitsPool + eobInflationAccrual;

    // 3. Draw. Always indemnity: the full pro-rated cap is paid regardless of
    // cost, limited only by what the pools hold. Acceleration first, then
    // Extension.
    const totalAvailable = openState.pools.baseBenefitPool + eobAfterAccrual;
    const targetDraw = eligibleDays > 0 ? Math.min(proRatedCap, totalAvailable) : 0;
    const drawFromAccelerated = Math.min(targetDraw, openState.pools.baseBenefitPool);
    const drawFromExtended = Math.min(
      targetDraw - drawFromAccelerated,
      eobAfterAccrual,
    );
    const rowPayout = drawFromAccelerated + drawFromExtended;

    const baseAfterDraw = openState.pools.baseBenefitPool - drawFromAccelerated;
    const eobAfterDraw = eobAfterAccrual - drawFromExtended;

    // 4. Retroactive EP lump, if this row satisfied the waiting period.
    //
    // This is UNCONDITIONAL, and deliberately so. ICC25-NWLA-686.1 p.3 makes the
    // retroactive payment a term of the contract — there is no variant of
    // CareMatters Annuity that serves an elimination period without paying it
    // back — so the stored, user-editable
    // `hasRetroactiveWaitingPeriodReimbursement` column is intentionally IGNORED
    // here, the same treatment secureCareStrategy gives it and the opposite of
    // cmTwoStrategy, where it is a real election.
    //
    // Reading the column instead would let an advisor model a policy the carrier
    // does not sell, and would silently drop the lump from every already-uploaded
    // annuity policy (the column defaults to false). The form no longer offers
    // the toggle for this product — see honorsRetroactiveWaitingPeriodElection in
    // FundingPolicyTypeDef, which is the single list of who honours it.
    const openRelevantWp =
      phaseCarePlan.appliedCareEnvironment === CareEnvironment.home
        ? activeWaitingPeriodBefore.homeDays
        : activeWaitingPeriodBefore.facilityDays;
    const unpaidWpDaysServed = activeUnpaidWpDaysServed + wpDaysThisRow;
    const epResult = computeEpRetroactiveLump({
      openRelevantWp,
      closeRelevantWp,
      unpaidWpDaysServed,
      baseAfterDraw,
      eobAfterDraw,
      monthlyCap,
      alreadyReimbursed: activeEpReimbursed,
    });

    // 5. Indemnity surplus.
    //
    // THE REGULAR ROW PAYMENT AND THE EP LUMP ARE SEPARATE STREAMS and must
    // stay that way. `getMonthPayout` in projectionTotals computes
    // `max(indemnityPayout, ltcCoveragePaid) + epLumpSumPaid`, reading the lump
    // from strategyDeltas — so folding the lump into `indemnityPayout` as well
    // makes every consumer count it twice (cash, coverage, policy value, ROI).
    // Measured: a 16,666.67 row reported as 29,166.67. Same split SecureCare
    // uses.
    // Exhaustion is judged on the OPENING pools (after this row's accrual), not
    // on what is left after paying. sharedSurplus is explicit about the
    // boundary: "The boundary row that EXHAUSTS the policy mid-row still had
    // drawable benefit at open (it pays a partial benefit), so it drains; the
    // freeze begins on the first fully-spent row after it."
    //
    // Subtracting this row's payout froze the reserve one row early, so banked
    // surplus could not cover the remaining cost on the final partial-payment
    // row. Same shape secureCare and cmTwo use.
    const policyExhausted = totalAvailable <= 0;
    const surplusResult = applyIndemnitySurplus({
      openSurplus: openState.strategyState.indemnitySurplus,
      indemnityPayout: rowPayout,
      eligibleCost,
      fullCost: costMetrics.effectiveCareCost,
      policyExhausted,
    });

    // The lump reimburses days already served out of pocket, so it covers this
    // row's REMAINING cost first and only the excess banks as surplus. Emitting
    // `epLumpSumAppliedToCost` is what stops the coverage rollup falling back to
    // counting the whole lump against cost.
    const costAfterCoverage = Math.max(
      costMetrics.effectiveCareCost - surplusResult.ltcPayoutToCost,
      0,
    );
    const epLumpSumAppliedToCost = Math.min(epResult.epLumpSumPaid, costAfterCoverage);
    const epLumpExcess = epResult.epLumpSumPaid - epLumpSumAppliedToCost;

    const waitingPeriodSurplusApplied = applyOpeningSurplusToCost({
      openSurplus: openState.strategyState.indemnitySurplus,
      alreadyApplied: surplusResult.surplusApplied,
      remainingCost: Math.max(costAfterCoverage - epLumpSumAppliedToCost, 0),
      enabled: wpDaysThisRow > 0,
      policyExhausted,
    });
    const totalSurplusApplied = surplusResult.surplusApplied + waitingPeriodSurplusApplied;
    // Cost covered by the REGULAR benefit plus drained surplus. The EP lump's
    // applied portion is deliberately NOT folded in here: the shared aggregate
    // `getTotalLtcCoveragePayout` adds `epLumpSumAppliedToCost` itself, so
    // including it would count every reimbursed dollar twice in LTC coverage and
    // every downstream policy-value total. CM II and SecureCare both store it
    // this way; `ineligibleCost` below still nets the lump off, which is where
    // the reimbursement legitimately reduces out-of-pocket.
    const ltcPayoutToCost = surplusResult.ltcPayoutToCost + waitingPeriodSurplusApplied;
    const totalCostRelieved = ltcPayoutToCost + epLumpSumAppliedToCost;
    const closeSurplus =
      surplusResult.closeSurplus - waitingPeriodSurplusApplied + epLumpExcess;
    // The lump's excess over this row's cost banks as surplus, so it has to be
    // REPORTED as accrued too — assertProjectionIdentities checks
    // close == open + accrued - applied every month and will reject a silent
    // balance change.
    const surplusAccrued = surplusResult.surplusAccrued + epLumpExcess;

    // 6. Contract value roll-forward. Acceleration draws (regular and EP)
    // deplete it dollar for dollar; interest credits monthly on what remains.
    // In a month with a withdrawal the rider charge is waived, matching the
    // ledger's no-charge behaviour once benefits are being paid.
    const acceleratedDrawTotal = drawFromAccelerated + epResult.epLumpSumBasePaid;
    const contractValueAfterDraw = Math.max(
      openState.pools.deathBenefit - acceleratedDrawTotal,
      0,
    );
    // True when a prior row in the merged stream already covered this calendar
    // month — i.e. the two spouses overlap here. Used both for the once-per-month
    // contract-value roll-forward below and for accumulating the waiver flag.
    const monthAlreadyRolledForward =
      priorMonth != null && priorMonth.context.monthIndex === monthIndex;

    // The waiver is a CONTRACT-level fact: "a Rider benefit has been paid during
    // the prior Contract Month" is true if EITHER insured was paid. On a joint
    // contract the merged stream emits two rows for an overlapping month, and
    // each row used to overwrite this flag with only its own result — so a month
    // where the client was paid but the partner was still serving their EP
    // closed the shared flag `false` (partner rows sort second), and the next
    // month charged the rider despite a benefit having been paid.
    //
    // OR across rows sharing a monthIndex; the accumulator resets when the
    // calendar month advances, which `monthAlreadyRolledForward` already
    // identifies.
    const benefitPaidOnThisRow = rowPayout + epResult.epLumpSumPaid > 0;
    // On a second row within the same month, `openState.benefitPaidLastMonth` is
    // the FIRST row's result (that row closed it), so OR-ing against it
    // accumulates the month. On a first row it is genuinely the previous
    // month's flag and must not be folded in.
    const benefitPaidThisMonth =
      benefitPaidOnThisRow ||
      (monthAlreadyRolledForward ? openState.strategyState.benefitPaidLastMonth : false);
    // Rider disclosure ICC25-NWLA-686.1 p.2 of 4, identical in all 23 sampled
    // illustrations: "Waiver of Rider Charges. The monthly Rider charge is
    // waived when a Rider benefit has been paid during the PRIOR Contract
    // Month."
    //
    // So the waiver lags the benefit by a month, and does NOT key on the
    // current month — which is what this originally did, copied from ForeCare.
    // The difference is one charged month at the start of a claim and one
    // waived month after it ends; on a long claim the two roughly cancel, and a
    // single month's charge is about 0.035% of contract value. Small, but it is
    // stated in the contract, so it is modelled rather than approximated.
    const waiveRiderCharge = openState.strategyState.benefitPaidLastMonth;
    const monthlyRate = monthlyEffectiveRate(config.guaranteedInterestRate);

    // The contract value rolls forward ONCE PER CALENDAR MONTH, not once per
    // row. There is one contract, so when both spouses claim in the same month
    // the merged stream emits two rows for that `monthIndex` and applying
    // interest and the rider charge on each credits the month twice.
    //
    // Measured on an overlapping 36-month joint claim before this: 36 of 36
    // overlapping months took two interest credits (and two rider charges in
    // two of them), compounding the contract value — and so the death benefit
    // and every cap derived from it — well above the contract.
    //
    // `priorMonth` is the previous row in the MERGED stream, so a prior row with
    // the same monthIndex means this month has already been rolled forward.
    // Both spouses still draw on their own rows; only the roll-forward is
    // once-per-month.

    let contractValueRiderCharge = 0;
    let contractValueInterestCredit = 0;
    let closeContractValue = contractValueAfterDraw;
    if (contractValueAfterDraw > 0 && !monthAlreadyRolledForward) {
      if (waiveRiderCharge) {
        contractValueInterestCredit = contractValueAfterDraw * monthlyRate;
        closeContractValue = contractValueAfterDraw + contractValueInterestCredit;
      } else {
        contractValueRiderCharge = contractValueAfterDraw * config.monthlyRiderChargeRate;
        const afterCharge = Math.max(contractValueAfterDraw - contractValueRiderCharge, 0);
        contractValueInterestCredit = afterCharge * monthlyRate;
        closeContractValue = Math.max(
          afterCharge + contractValueInterestCredit,
          contractValueAfterDraw,
        );
      }
    }

    const closeBase = Math.max(baseAfterDraw - epResult.epLumpSumBasePaid, 0);
    const closeEob = Math.max(eobAfterDraw - epResult.epLumpSumEobPaid, 0);
    const closeDeathBenefit = closeContractValue;
    const deathBenefitReduction = openState.pools.deathBenefit - closeDeathBenefit;
    const contractValueNetAccrual = closeContractValue - contractValueAfterDraw;
    const inExtendedPhase =
      drawFromExtended > 0 || (openState.pools.baseBenefitPool <= 0 && drawFromAccelerated === 0);

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      careCostInflationYears: costMetrics.careCostInflationYears,
      projectedCareYear: costMetrics.projectedCareYear,
      non2AdlDays,
      coverageEligibleDays: eligibleDays,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      appliedMonthlyBenefitCap: monthlyCap,
      appliedCapPoolSource: inExtendedPhase ? 'eob' : 'base',
    };

    const payout: PayoutSummary = {
      ltcCoveragePaid: surplusResult.ltcCoveragePaid,
      ltcPayoutToCost,
      ineligibleCost: costMetrics.effectiveCareCost - totalCostRelieved,
      // Regular row payment ONLY. The retroactive lump is reported through
      // `epLumpSumPaid` in strategyDeltas and added on by getMonthPayout.
      indemnityPayout: rowPayout,
    };

    const deltas: MonthlyDeltas<CareMattersAnnuityDeltas> = {
      payout,
      base: {
        depletion: drawFromAccelerated + epResult.epLumpSumBasePaid,
        inflationAccrual: 0,
      },
      eob: {
        depletion: drawFromExtended + epResult.epLumpSumEobPaid,
        inflationAccrual: eobInflationAccrual,
      },
      deathBenefit: {
        reduction: deathBenefitReduction,
        // No guaranteed minimum to floor against — the residual simply runs out.
        flooredAtMinimum: false,
      },
      strategyDeltas: {
        contractValueInterestCredit,
        contractValueRiderCharge,
        acceleratedBenefitDraw: drawFromAccelerated,
        extendedBenefitDraw: drawFromExtended,
        indemnitySurplusAccrued: surplusAccrued,
        indemnitySurplusApplied: totalSurplusApplied,
        epLumpSumPaid: epResult.epLumpSumPaid,
        epLumpSumAppliedToCost,
        epLumpSumBasePaid: epResult.epLumpSumBasePaid,
        epLumpSumEobPaid: epResult.epLumpSumEobPaid,
        epLumpSumDays: epResult.epLumpSumDays,
        epLumpSumDailyRate:
          epResult.epLumpSumDays > 0 ? epResult.epLumpSumPaid / epResult.epLumpSumDays : 0,
      },
    };

    const closeState: MonthlyState<CareMattersAnnuityState> = {
      pools: {
        baseBenefitPool: closeBase,
        extensionOfBenefitsPool: closeEob,
        deathBenefit: closeDeathBenefit,
      },
      waitingPeriod: { homeDays: closeHomeWp, facilityDays: closeFacilityWp },
      strategyState: {
        indemnitySurplus: closeSurplus,
        epReimbursed: activeEpReimbursed || epResult.fired,
        unpaidWpDaysServed,
        benefitPaidLastMonth: benefitPaidThisMonth,
        claimStartMonthlyCap: openState.strategyState.claimStartMonthlyCap,
        // Joint: fold this row's progress back into the ACTIVE claimant's slot
        // and leave the other spouse's untouched.
        ...(isJointRun && jointWaitingPeriodBefore
          ? {
              jointWaitingPeriod: {
                ...jointWaitingPeriodBefore,
                [activeClaimant]: { homeDays: closeHomeWp, facilityDays: closeFacilityWp },
              },
              jointEpReimbursed: {
                ...(openState.strategyState.jointEpReimbursed ?? {
                  client: false,
                  partner: false,
                }),
                [activeClaimant]: activeEpReimbursed || epResult.fired,
              },
              jointUnpaidWpDaysServed: {
                ...(openState.strategyState.jointUnpaidWpDaysServed ?? {
                  client: 0,
                  partner: 0,
                }),
                [activeClaimant]: unpaidWpDaysServed,
              },
            }
          : {}),
      },
    };

    const status: MonthlyStatus = {
      ltcPoolExhausted: closeBase + closeEob <= 0,
      clientPoolExhausted: closeBase + closeEob <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    const milestones = deriveStandardMilestones<CareMattersAnnuityState, CareMattersAnnuityDeltas>(
      {
        context,
        facts,
        openState,
        closeState,
        deltas,
        // Transition milestones (phase/environment change, anniversary, claim
        // start) must key off the SAME claimant's previous row. On a joint run
        // the global `priorMonth` is often the other spouse, which would fire
        // them spuriously on every interleaved row.
        priorMonth: isJointRun ? priorClaimantMonth : priorMonth,
        retroactiveLumpPaid: epResult.epLumpSumPaid,
        // The residual contract value can fall to $0 and can also grow by
        // guaranteed interest mid-claim, so the generic minimum-DB milestone is
        // not the right semantics here — same reasoning as ForeCare's ledger.
        emitMinimumDeathBenefitMilestone: false,
        clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
        // The benefit grows pre-claim off the contract value even with no rider
        // elected, so this is true whenever either engine is moving.
        hasInflationProtection: config.inflationRate > 0 || contractValueNetAccrual > 0,
      },
    );

    const ineligibilityInput = {
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState,
      closeState,
      payout,
    };

    // Surface the active claimant's WP-before on the row, so identity checks and
    // the UI read per-claimant rather than whichever spouse happened to be last.
    const rowOpenState: MonthlyState<CareMattersAnnuityState> = isJointRun
      ? { ...openState, waitingPeriod: activeWaitingPeriodBefore }
      : openState;

    return {
      context,
      facts,
      openState: rowOpenState,
      state: closeState,
      deltas,
      events: {
        status,
        milestones,
        ineligibilityReason: deriveIneligibilityReason<CareMattersAnnuityState>(ineligibilityInput),
        ineligibleDayBreakdown:
          deriveIneligibleDayBreakdown<CareMattersAnnuityState>(ineligibilityInput),
      },
    };
  },

  // Whatever contract value LTC did not consume (p.4).
  finalizeDeathBenefit({
    closingState,
  }: FinalizeDeathBenefitInput<CareMattersAnnuityState>): number {
    return closingState.pools.deathBenefit;
  },
};
